<template>
    <label :for="id" class="flex flex-row gap-4 items-center">
        <CheckboxRoot
            :id="id"
            v-slot="{ state }"
            v-bind="$attrs"
            :class="[shape === 'circle' ? 'rounded-full' : 'rounded']"
            class="flex h-5 min-w-5 max-w-5 appearance-none items-center justify-center overflow-hidden bg-control-bg border border-control-border-strong"
            @update:model-value="$emit('update:modelValue', $event)">
            <CheckboxIndicator class="bg-control-bg h-full w-full flex items-center justify-center">
                <!-- Both glyph sets render because the tonal flag is DOM state (html[data-theme]) that
                     JS cannot read; the style block below picks the one for the active theme. -->
                <i class="bi text-control-accent text-base/none legacy-glyph" :class="legacyIconClass(state === 'indeterminate')" aria-hidden="true"></i>
                <i class="bi text-control-accent text-base/none tonal-glyph" :class="state === 'indeterminate' ? 'bi-dash-lg' : 'bi-check-lg'" aria-hidden="true"></i>
            </CheckboxIndicator>
        </CheckboxRoot>
        <span v-if="$slots.default" class="select-none">
            <slot></slot>
        </span>
    </label>
</template>

<script setup lang="ts">
import { CheckboxIndicator, CheckboxRoot, type CheckboxRootEmits } from "reka-ui";
import { computed, useId } from "vue";

type BaseCheckboxProps = {
    checkboxId?: string;
    isFilled?: boolean;
    shape?: "circle" | "square";
};

const { isFilled = true, shape = "circle", checkboxId } = defineProps<BaseCheckboxProps>();

const id = computed(() => checkboxId ?? useId());

defineEmits<CheckboxRootEmits>();

function legacyIconClass(indeterminate: boolean) {
    let iconName = indeterminate ? "bi-dash" : "bi-check";
    iconName += shape === "circle" ? "-circle" : "-square";
    return isFilled ? `${iconName}-fill` : iconName;
}
</script>

<style scoped>
/* Tonal glyphs per the design-system source's 27 Aug changelog; gated in CSS
   so the flag-off render keeps today's icons exactly. */
.tonal-glyph {
    display: none;
}

html[data-theme="tonal"] .legacy-glyph {
    display: none;
}

html[data-theme="tonal"] .tonal-glyph {
    display: inline-block;
}

/* The spec's checked state is a filled box with a white tick, not an outline
   with an ink tick: box fills with the control accent (neutral-800 under the
   flag) when checked or indeterminate, and the plain-stroke glyphs read white
   on it. Flag off is untouched: the -fill glyph family paints its own box. */
html[data-theme="tonal"] [data-state="checked"],
html[data-theme="tonal"] [data-state="indeterminate"] {
    background-color: var(--color-control-accent);
    border-color: var(--color-control-accent);
}

html[data-theme="tonal"] [data-state="checked"] :deep(.bg-control-bg),
html[data-theme="tonal"] [data-state="indeterminate"] :deep(.bg-control-bg) {
    background-color: transparent;
}

html[data-theme="tonal"] [data-state="checked"] .tonal-glyph,
html[data-theme="tonal"] [data-state="indeterminate"] .tonal-glyph {
    color: #ffffff;
}
</style>
